/**
 * Thrivia Embed SDK: host platform detection (spec section 7).
 *
 * Purpose: work out what kind of website we have landed on, before anything renders. The answer is
 * tagged onto every API call and every beacon, and it decides one behavioural thing later: a
 * sandboxed host (Wix) cannot host our checkout overlay, so Task 6 opens a popup window instead.
 *
 * Every probe is wrapped in try/catch and every failure resolves to "we do not know", because this
 * code runs inside somebody else's page and a detection routine is never worth an exception on an
 * organizer's homepage. The cross-origin probe in particular is EXPECTED to throw: a SecurityError
 * from `window.top.location` is not an error condition, it IS the signal that we are framed by a
 * document we cannot reach.
 *
 * Used by: `index.ts` (boot), `api.ts` (the `platform` query parameter), `track.ts` (beacons).
 * Task 6 reads `sandboxed` to choose popup over overlay.
 *
 * No runtime dependencies, DOM only, ES2019 output target.
 */

/**
 * The vocabulary sent to the backend, and it is a CLOSED list on both sides. The server holds the
 * `platform` query parameter to `/^[a-z0-9][a-z0-9._-]{0,31}$/` and DISCARDS anything else as
 * `"unknown"` - it does not truncate, so a longer or differently-cased id is not shortened, it is
 * thrown away. Every value below is stored on the embed session as written and is what a creator
 * eventually sees in their dashboard breakdown, so a new one has to fit that expression.
 *
 * `framed` means "inside a cross-origin frame we could not identify" and `web` means "an ordinary
 * top-level page", which is the custom-site and self-hosted case.
 */
export type PlatformId =
  | "squarespace"
  | "webflow"
  | "wordpress"
  | "wix"
  | "framed"
  | "web";

export interface PlatformInfo {
  id: PlatformId;
  /** True when this document is not the top-level one, whatever the framing origin. */
  framed: boolean;
  /**
   * True when we are framed by a document we cannot reach across origins, which is the Wix HTML
   * component case and any `sandbox`ed iframe.
   *
   * Task 6's contract depends on this: a checkout overlay drawn inside a fixed-height, cross-origin
   * embed box is unusable, so those hosts get a popup window opened synchronously inside the click
   * handler instead. Popup blockers kill anything opened after an await, which is why the decision
   * has to be available before the click rather than fetched during it.
   */
  sandboxed: boolean;
}

/**
 * Hosts Wix serves its own pages and HTML components from. A Wix "Embed a widget" component runs on
 * a wix-owned host inside an iframe on the customer's domain, so either our own hostname or the
 * referring page identifies it.
 */
const WIX_HOST_RE = /(^|\.)(wix\.com|wixsite\.com|wixstatic\.com|filesusr\.com|editorx\.com)$/i;

/** WordPress serves its themes and core scripts from these two paths on every installation. */
const WORDPRESS_SELECTOR =
  'script[src*="/wp-content/"],script[src*="/wp-includes/"],link[href*="/wp-content/"]';

interface HostGlobals {
  Static?: { SQUARESPACE_CONTEXT?: unknown };
  Webflow?: unknown;
}

/**
 * Probes the framing situation.
 *
 * Two separate throw sites, and both are load-bearing. `window.self !== window.top` can itself
 * throw inside a `sandbox`ed frame, which already tells us everything: framed and unreachable.
 * Reading `window.top.location.href` throws a SecurityError whenever the top document is
 * cross-origin, which is precisely the condition Task 6 branches on.
 */
function probeFrame(): { framed: boolean; sandboxed: boolean } {
  let framed: boolean;
  try {
    framed = window.self !== window.top;
  } catch {
    return { framed: true, sandboxed: true };
  }
  if (!framed) return { framed: false, sandboxed: false };

  try {
    // Reading, not using. The read is the probe.
    void (window.top as Window).location.href;
    return { framed: true, sandboxed: false };
  } catch {
    return { framed: true, sandboxed: true };
  }
}

/** Hostname of the referring document, or "" when there is none or it is unparseable. */
function referrerHost(): string {
  try {
    return document.referrer ? new URL(document.referrer).hostname : "";
  } catch {
    return "";
  }
}

/**
 * Whether we are running on Wix.
 *
 * `window.location` is consulted here and ONLY here in the whole bundle. That is deliberate and it
 * is not the mistake `origin.ts` exists to prevent: this asks "whose page am I standing on", which
 * is exactly what `location` answers, rather than "where was I served from", which it answers
 * wrongly. No URL is ever built from it.
 *
 * The referrer is only consulted when we are framed. On a top-level page the referrer is simply
 * wherever the visitor clicked in from, so a visitor arriving from wix.com to an unrelated custom
 * site would otherwise be reported as a Wix install for the rest of the page's life.
 */
function isWix(framed: boolean): boolean {
  try {
    if (WIX_HOST_RE.test(window.location.hostname)) return true;
  } catch {
    /* ignore */
  }
  return framed && WIX_HOST_RE.test(referrerHost());
}

/**
 * Detects the host platform. Called once at boot; the result is immutable for the page's lifetime.
 *
 * Order matters. The frame probe runs first because Wix's signature is a frame plus a wix-owned
 * host, and a Wix site that also loads Squarespace-looking globals (it will not, but a customer's
 * pasted third-party widget might) must still be treated as sandboxed. Builder globals are then
 * checked most-specific first, and the ordinary top-level page falls through to "web".
 */
export function detectPlatform(): PlatformInfo {
  const frame = probeFrame();
  const w = window as unknown as HostGlobals;

  // `framed` reports only the UNREACHABLE frame: a same-origin frame on an organizer's own site
  // behaves exactly like a top-level page for everything we do, so calling it out would split the
  // reporting without telling a creator anything. The raw fact stays available on `frame.framed`.
  let id: PlatformId = frame.sandboxed ? "framed" : "web";

  try {
    if (isWix(frame.framed)) id = "wix";
    else if (w.Static && w.Static.SQUARESPACE_CONTEXT) id = "squarespace";
    else if (w.Webflow) id = "webflow";
    else if (document.querySelector(WORDPRESS_SELECTOR)) id = "wordpress";
  } catch {
    /* Detection is best effort. A hostile or exotic host must not break the widget. */
  }

  return { id: id, framed: frame.framed, sandboxed: frame.sandboxed };
}
